import React, { useEffect, useState } from 'react';
import { Card, Title, Col as TremorCol } from '@tremor/react';
import { IonGrid, IonRow, IonCol, IonButton, IonText, IonIcon, IonPopover } from '@ionic/react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { chevronForward, informationCircle } from 'ionicons/icons';
import { colorTheme } from '../../utils/constants';

ChartJS.register(ArcElement, Tooltip, Legend);

const CurrentMonthPaymentsCard = ({ billedThisMonth = 0, totalPaid = 0, overPaid = 0, currency, percentagePaid = 0, handleRecordPayment, onClick, startDate, endDate, onStartDateChange, onEndDateChange }) => {
    const [showBilledToolTip, setShowBilledToolTip] = useState(false);
    const [showPaidToolTip, setShowPaidToolTip] = useState(false);
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    useEffect(() => {
        const checkTheme = () => {
            const darkThemeMq = window.matchMedia("(prefers-color-scheme: dark)");
            setIsDarkTheme(darkThemeMq.matches);
        };

        checkTheme();

        window.matchMedia("(prefers-color-scheme: dark)").addEventListener('change', (e) => {
            setIsDarkTheme(e.matches);
        });

        return () => {
            window.matchMedia("(prefers-color-scheme: dark)").removeEventListener('change', (e) => {
                setIsDarkTheme(e.matches);
            });
        };
    }, []);

    const doughnutData = {
        datasets: [
            {
                data: [percentagePaid, 100 - percentagePaid],
                backgroundColor: [
                    isDarkTheme ? colorTheme.primary["350"] : colorTheme.primary["700"],
                    colorTheme.primary["50"]
                ],
                hoverBackgroundColor: [
                    isDarkTheme ? colorTheme.primary["400"] : colorTheme.primary["800"],
                    colorTheme.primary["100"]
                ],
                borderWidth: 0
            }
        ]
    };

    const doughnutOptions = {
        cutout: '70%',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                enabled: false
            },
            legend: {
                display: false
            },
        }
    };

    return (
        <TremorCol numColSpan={1} numColSpanLg={2}>
            <Card className="mb-3" style={{ height: "fit-content", borderRadius: "10px" }}>
                <Title className="mb-2 text-center">
                    Payments and Invoices
                </Title>
                <IonGrid>
                    <IonRow className="w-100 ion-justify-content-center ion-align-items-center mb-2">
                        <IonCol size="6" className="ion-padding-horizontal">
                            <input
                                type="date"
                                id="paymentsStartDate"
                                name="paymentsStartDate"
                                value={startDate || ''}
                                onChange={onStartDateChange}
                                style={{ width: '100%', textAlign: 'center', border: "1px solid gray", padding: '8px' }}
                            />
                        </IonCol>
                        <IonCol size="6" className="ion-padding-horizontal">
                            <input
                                type="date"
                                id="paymentsEndDate"
                                name="paymentsEndDate"
                                value={endDate || ''}
                                onChange={onEndDateChange}
                                style={{ width: '100%', textAlign: 'center', border: "1px solid gray", padding: '8px' }}
                            />
                        </IonCol>
                    </IonRow>
                </IonGrid>
                <IonGrid>
                    <IonRow className="d-flex align-items-center justify-content-center flex-column flex-md-row">
                        <IonCol size="12" sizeMd="3" className="text-center d-none d-md-block order-1 order-md-1 mb-2 mb-md-0">
                            <IonText color="primary" style={{ fontSize: '1.5em' }}>{billedThisMonth.toLocaleString("en-US")}</IonText>
                            <h6>
                                Total Billed
                                <IonIcon id="billedToolTip" icon={informationCircle} className="ml-2" onClick={() => setShowBilledToolTip(true)} />
                            </h6>
                            <IonPopover
                                isOpen={showBilledToolTip}
                                onDidDismiss={() => setShowBilledToolTip(false)}
                            >
                                <IonText className="p-2">This is the total amount billed this month.</IonText>
                            </IonPopover>
                            <IonText color="primary" style={{ fontSize: '1.5em' }}>{totalPaid.toLocaleString("en-US")}</IonText>
                            <h6>
                                Total Paid
                                <IonIcon id="paidToolTip" icon={informationCircle} className="ml-2" onClick={() => setShowPaidToolTip(true)} />
                            </h6>
                            <IonPopover
                                isOpen={showPaidToolTip}
                                onDidDismiss={() => setShowPaidToolTip(false)}
                            >
                                <IonText className="p-2">This is the total amount paid this month.</IonText>
                            </IonPopover>
                        </IonCol>
                        <IonCol size="12" sizeMd="3" className="text-center d-md-none order-1 order-md-1 mb-2 mb-md-0">
                            <IonRow>
                                <IonCol size="6">
                                    <IonText color="primary" style={{ fontSize: '1.5em' }}>{billedThisMonth.toLocaleString("en-US")}</IonText>
                                    <h6>
                                        Total Billed
                                        <IonIcon id="billedToolTipMobile" icon={informationCircle} className="ml-2" onClick={() => setShowBilledToolTip(true)} />
                                    </h6>
                                    <IonPopover
                                        isOpen={showBilledToolTip}
                                        onDidDismiss={() => setShowBilledToolTip(false)}
                                    >
                                        <IonText className="p-2">This is the total amount billed this month.</IonText>
                                    </IonPopover>
                                </IonCol>
                                <IonCol size="6">
                                    <IonText color="primary" style={{ fontSize: '1.5em' }}>{totalPaid.toLocaleString("en-US")}</IonText>
                                    <h6>
                                        Total Paid
                                        <IonIcon id="paidToolTipMobile" icon={informationCircle} className="ml-2" onClick={() => setShowPaidToolTip(true)} />
                                    </h6>
                                    <IonPopover
                                        isOpen={showPaidToolTip}
                                        onDidDismiss={() => setShowPaidToolTip(false)}
                                    >
                                        <IonText className="p-2">This is the total amount paid this month.</IonText>
                                    </IonPopover>
                                </IonCol>
                            </IonRow>
                        </IonCol>
                        <IonCol size="12" sizeMd="5" className="text-center order-2 order-md-2 mb-2 mb-md-0">
                            <div style={{ position: 'relative', width: '125px', height: '125px', margin: '0 auto' }}>
                                <Doughnut data={doughnutData} options={doughnutOptions} />
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '24px',
                                    color: colorTheme.primary["700"]
                                }}>
                                    <IonText color={"primary"}>{percentagePaid}%</IonText>
                                </div>
                            </div>
                        </IonCol>
                        <IonCol size="12" sizeMd="4" className="text-center order-3 order-md-3">
                            <div className="d-flex flex-column align-items-center">
                                <IonButton
                                    onClick={handleRecordPayment}
                                    fill='outline'
                                    style={{
                                        borderRadius: "10px",
                                        height: "fit-content",
                                        marginBottom: '10px',
                                        width: 'min-content',
                                        minWidth: '100%'
                                    }}
                                >
                                    Record Payments
                                </IonButton>
                            </div>
                        </IonCol>
                    </IonRow>
                    <hr color='lightgray' className='mt-2' />
                    <IonRow className="d-flex mt-2 justify-content-end align-items-end">
                        <IonButton id='view-payments-button' onClick={() => onClick()} size="lg" fill='outline' variant="primary">
                            View <IonIcon icon={chevronForward} />
                        </IonButton>
                    </IonRow>
                </IonGrid>
            </Card>
        </TremorCol>
    );
}

export default CurrentMonthPaymentsCard;
